import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccessPage = () => {
  const { t } = useLanguage();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('checking');
  const [orderId, setOrderId] = useState(null);
  
  // PayPal params
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  const orderIdParam = searchParams.get('order_id');
  const isPayPal = searchParams.get('paypal') === 'true';

  useEffect(() => {
    if (isPayPal && paymentId && payerId) {
      executePayPalPayment();
    } else if (orderIdParam && !paymentId) {
      // Direct success (no payment needed or already processed)
      setStatus('success');
      setOrderId(orderIdParam);
      clearCart();
    } else {
      setStatus('error');
    }
  }, [paymentId, payerId, isPayPal]);

  const executePayPalPayment = async () => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/paypal/execute?payment_id=${paymentId}&payer_id=${payerId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setStatus('success');
        setOrderId(response.data.order_id);
        clearCart();
        toast.success('Pagamento completato!');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('PayPal execute error:', error);
      setStatus('error');
      toast.error('Errore durante il pagamento');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center py-16" data-testid="order-processing">
        <div className="text-center max-w-md mx-auto px-6">
          <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold font-serif mb-4">Elaborazione Pagamento...</h1>
          <p className="text-muted-foreground">
            Stiamo completando il tuo pagamento PayPal. Attendi un momento.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center py-16" data-testid="order-success">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold font-serif mb-4">{t('orderConfirmed')}</h1>
          <p className="text-muted-foreground mb-2">{t('thankYou')}</p>
          {orderId && (
            <p className="text-sm text-muted-foreground mb-8">
              {t('orderNumber')}: <span className="font-mono font-medium">{orderId.slice(0, 8).toUpperCase()}</span>
            </p>
          )}
          
          <div className="bg-muted/50 rounded-2xl p-6 mb-8 text-left">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Prossimi Passi
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Riceverai una email di conferma</li>
              <li>• Il tuo ordine verrà elaborato entro 24 ore</li>
              <li>• Ti invieremo un aggiornamento quando sarà spedito</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/products">
              <Button className="btn-primary w-full sm:w-auto">
                {t('continueShopping')}
              </Button>
            </Link>
            <Link to="/support">
              <Button variant="outline" className="w-full sm:w-auto">
                {t('support')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center py-16" data-testid="order-error">
      <div className="text-center max-w-md mx-auto px-6">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">⚠️</span>
        </div>
        <h1 className="text-2xl font-bold font-serif mb-4">Errore di Pagamento</h1>
        <p className="text-muted-foreground mb-8">
          Si è verificato un errore durante il pagamento. Riprova o contatta il supporto.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/cart">
            <Button className="btn-primary w-full sm:w-auto">
              Torna al Carrello
            </Button>
          </Link>
          <Link to="/support">
            <Button variant="outline" className="w-full sm:w-auto">
              Contatta Supporto
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
